import React, { useState, useEffect } from "react";
import Header from "../FleetHeader/Header";
import CarouselSection from '../CarouselSection/CarouselSection';
import { cars } from '../../services/api';
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

const CarFleet = () => {
  const [filters, setFilters] = useState({
    economy: [],
    electric: [],
    standard: [],
  });

  const [carData, setCarData] = useState({
    economy: [],
    electric: [],
    standard: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await cars.getAll();
        const today = new Date();
        
        // Get today's date for availability check
        const todayStr = today.toISOString();
        
        // Fetch current price for all cars in one request
        const availabilityResponse = await cars.getAvailable({
          startDate: todayStr,
          endDate: todayStr,
          includeAll: true
        });
        
        // Create a map of car IDs to dynamic prices
        const dynamicPriceMap = {};
        availabilityResponse.data.forEach(car => {
          dynamicPriceMap[car._id] = car.dynamicPrice;
        });
        
        // Filter cars to only show active/available ones
        const activeCars = response.data.filter(car => 
          car.status === 'available' || car.status === 'rented' || !car.status // Include cars without status field for backward compatibility
        );
        
        // Enhance cars with dynamic prices
        const allCars = activeCars.map(car => ({
          ...car,
          name: `${car.make} ${car.model} ${car.transmission.charAt(0).toUpperCase() + car.transmission.slice(1)} (or Similar)`,
          image: car.images[0], // Use first image from images array
          dynamicPrice: dynamicPriceMap[car._id] || car.basePrice // Use dynamic price if available, otherwise use base price
        }));
        
        // Group cars by category
        const grouped = {
          economy: allCars.filter(car => car.category === 'Economy'),
          electric: allCars.filter(car => car.category === 'Electric'),
          standard: allCars.filter(car => car.category === 'Standard')
        };

        setCarData(grouped);
      } catch (err) {
        console.error('Error fetching cars:', err);
        setError('Failed to load cars. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  const filterCars = (cars, selectedFilters) => {
    if (!selectedFilters || selectedFilters.length === 0) return cars;
    
    return cars.filter(car => {
      return selectedFilters.every(filter => {
        if (filter.label.includes('Person')) {
          return car.persons === filter.value;
        }
        if (filter.label.includes('Suitcase')) {
          return car.suitcases === filter.value;
        }
        if (filter.value === 'Manual' || filter.value === 'Automatic') {
          return car.transmission.toLowerCase() === filter.value.toLowerCase();
        }
        return true;
      });
    });
  };

  const sliderRefs = {
    economy: React.useRef(),
    electric: React.useRef(),
    standard: React.useRef(),
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center text-red-500 py-8">{error}</div>;
  }

  return (
    <>
      <section className="py-10">
        <div className="items-center mx-auto container">
          <h3 className="text-3xl text-center font-medium mb-4 w-full px-4 mx-auto Aleo tracking-wide">
            Explore Our Diverse <b>Mykonos Car Rental</b> Fleets
          </h3>
          <hr className="w-40 mb-4 mx-auto border-2 border-red-400" />
          <p className="text-center text-sm text-gray-400 mb-6 w-full lg:w-1/2 mx-auto Aleo tracking-wide">
            At <b>Mykonos Car Rentals,</b> we pride ourselves on offering a wide
            range of premium vehicles to cater to your every need and
            preference.
          </p>
        </div>
      </section>
      
      <section className="max-w-[1440px] mx-auto px-8">
        <div className="mb-12">
          <Header 
            title="Economy" 
            link="/economy"
            onNext={() => sliderRefs.economy.current.slickNext()}
            onPrev={() => sliderRefs.economy.current.slickPrev()}
            cars={carData.economy}
            onFilterChange={(selected) => setFilters(prev => ({ ...prev, economy: selected }))}
          />
          <CarouselSection 
            cars={filterCars(carData.economy, filters.economy)} 
            sliderRef={sliderRefs.economy} 
          />
        </div>

        <div className="mb-12">
          <Header 
            title="Electric" 
            link="/electric"
            onNext={() => sliderRefs.electric.current.slickNext()}
            onPrev={() => sliderRefs.electric.current.slickPrev()}
            cars={carData.electric}
            onFilterChange={(selected) => setFilters(prev => ({ ...prev, electric: selected }))}
          />
          <CarouselSection 
            cars={filterCars(carData.electric, filters.electric)} 
            sliderRef={sliderRefs.electric} 
          />
        </div>

        <div className="mb-12">
          <Header 
            title="Standard" 
            link="/standard"
            onNext={() => sliderRefs.standard.current.slickNext()}
            onPrev={() => sliderRefs.standard.current.slickPrev()}
            cars={carData.standard}
            onFilterChange={(selected) => setFilters(prev => ({ ...prev, standard: selected }))}
          />
          <CarouselSection 
            cars={filterCars(carData.standard, filters.standard)} 
            sliderRef={sliderRefs.standard} 
          />
        </div>
      </section>
    </>
  );
};

export default CarFleet;
